import React from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'April 15, 2026';

const SECTIONS = [
  {
    id: 'acceptance',
    title: '1. Acceptance of terms',
    body: 'By accessing or using any Lexint product or website (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). If you are using the Services on behalf of an organisation, you represent that you have the authority to bind that organisation to these Terms.',
  },
  {
    id: 'services',
    title: '2. Description of services',
    body: 'Lexint provides software products for SBOM analysis, vulnerability scanning, SAST, and code intelligence: DepGuard, Vigil, and PRISM (the "Products"). The Products are provided as software binaries for self-hosted deployment and/or as a SaaS platform accessible via the portal.',
  },
  {
    id: 'accounts',
    title: '3. Accounts and access',
    points: [
      'You are responsible for maintaining the confidentiality of your account credentials.',
      'You must notify us immediately at security@lexint.io of any unauthorised use of your account.',
      'Each account may only be used by one individual. Sharing credentials between users is prohibited.',
      'Enterprise accounts may create sub-accounts for team members within the contracted seat limit.',
    ],
  },
  {
    id: 'license',
    title: '4. License',
    body: 'Subject to your compliance with these Terms and payment of applicable fees, Lexint grants you a limited, non-exclusive, non-transferable, revocable licence to access and use the Services for your internal business purposes.',
    subpoints: [
      { heading: 'Permitted uses', items: ['Scan your own or your clients\' software supply chains', 'Integrate DepGuard into your CI/CD pipelines', 'Generate SBOM reports for compliance purposes'] },
      { heading: 'Prohibited uses', items: ['Reverse engineer, decompile, or disassemble any part of the Services', 'Use the Services to scan third-party systems without authorisation', 'Resell or sublicense the Services without a written reseller agreement', 'Remove or obscure any proprietary notices'] },
    ],
  },
  {
    id: 'data',
    title: '5. Your data',
    body: 'You retain all ownership of the data you upload to the Services, including SBOMs, scan configurations, and reports. You grant Lexint a limited licence to process your data solely to provide and improve the Services. Lexint will not sell, rent, or share your data with third parties except as described in our Privacy Policy.',
  },
  {
    id: 'payment',
    title: '6. Payment and subscriptions',
    points: [
      'Subscriptions are billed in advance on a monthly or annual basis.',
      'All fees are non-refundable except as required by applicable law.',
      'We may change pricing with 60 days written notice for annual plans and 30 days notice for monthly plans.',
      'Failure to pay may result in suspension of access. We will give 14 days notice before suspension.',
      'Enterprise contracts are governed by a separate Order Form and Master Service Agreement.',
    ],
  },
  {
    id: 'sla',
    title: '7. Service levels',
    table: [
      { plan: 'Community', uptime: 'Best effort', support: 'Community forum' },
      { plan: 'Starter', uptime: '99.5%', support: 'Email (2 business days)' },
      { plan: 'Professional', uptime: '99.9%', support: 'Email (1 business day)' },
      { plan: 'Enterprise', uptime: '99.95%', support: 'Dedicated SRE, 4-hour SLA' },
    ],
  },
  {
    id: 'ip',
    title: '8. Intellectual property',
    body: 'The Services, including all software, algorithms, documentation, and branding, are the exclusive property of Lexint B.V. and are protected by copyright, trade secret, and other intellectual property laws. PRISM, Vigil, DepGuard, and the Lexint name and logo are trademarks of Lexint B.V.',
  },
  {
    id: 'liability',
    title: '9. Limitation of liability',
    body: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, LEXINT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL. IN NO EVENT SHALL LEXINT\'S TOTAL LIABILITY EXCEED THE AMOUNTS PAID BY YOU IN THE 12 MONTHS PRECEDING THE CLAIM. Nothing in these Terms limits liability for fraud, death, or personal injury caused by negligence.',
  },
  {
    id: 'warranty',
    title: '10. Disclaimer of warranties',
    body: 'THE SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. LEXINT DOES NOT WARRANT THAT THE SERVICES WILL BE ERROR-FREE, UNINTERRUPTED, OR THAT ALL VULNERABILITIES WILL BE DETECTED. THE SERVICES ARE A TOOL TO ASSIST YOUR SECURITY PROCESSES — THEY DO NOT REPLACE PROFESSIONAL SECURITY REVIEW.',
  },
  {
    id: 'termination',
    title: '11. Termination',
    body: 'Either party may terminate the agreement at any time. You may cancel your subscription by providing 30 days written notice. Lexint may suspend or terminate your access immediately for material breach of these Terms. Upon termination, your right to use the Services ceases and you must delete all downloaded binaries.',
  },
  {
    id: 'governing-law',
    title: '12. Governing law and disputes',
    body: 'These Terms are governed by the laws of the Netherlands. Any dispute arising from these Terms shall be submitted to the competent courts in Amsterdam, the Netherlands, unless a mandatory consumer protection law in your country of residence applies. Enterprise contracts may specify alternative dispute resolution via a Master Service Agreement.',
  },
  {
    id: 'changes',
    title: '13. Changes to these terms',
    body: 'We may update these Terms from time to time. Material changes will be communicated by email to registered users at least 30 days before they take effect. Continued use of the Services after that date constitutes acceptance of the updated Terms.',
  },
  {
    id: 'contact-legal',
    title: '14. Contact',
    body: 'For legal enquiries, contract questions, or to report a Terms violation, contact legal@lexint.io or write to: Lexint B.V., Legal Department, The Netherlands.',
  },
];

export default function WebsiteTermsPage() {
  return (
    <div className="dgw-page-wrap">
      {/* Hero */}
      <section style={{background:'#0f172a', padding:'80px 24px 60px', textAlign:'center'}}>
        <div style={{maxWidth:760, margin:'0 auto'}}>
          <div className="dgw-badge" style={{margin:'0 auto 20px'}}>Legal</div>
          <h1 style={{fontSize:'clamp(28px,4vw,48px)', fontWeight:800, color:'#f1f5f9', margin:'0 0 16px', letterSpacing:'-1px'}}>
            Terms of Service
          </h1>
          <p style={{color:'#94a3b8', fontSize:16, margin:'0 0 12px'}}>
            Last updated: {LAST_UPDATED}
          </p>
          <p style={{color:'#64748b', fontSize:14}}>
            Please read these Terms carefully before using any Lexint product or service.
          </p>
        </div>
      </section>

      {/* Content */}
      <section style={{background:'#fff', padding:'64px 24px'}}>
        <div className="dgw-sidebar-layout" style={{maxWidth:800, margin:'0 auto'}}>
          {/* Sidebar TOC */}
          <div className="dgw-toc" style={{position:'sticky', top:100}}>
            <div style={{fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:1, marginBottom:12}}>Contents</div>
            <nav style={{display:'flex', flexDirection:'column', gap:8}}>
              {SECTIONS.map(s => (
                <a key={s.id} href={`#${s.id}`} style={{fontSize:13, color:'#64748b', textDecoration:'none', lineHeight:1.4}}
                  onMouseOver={e => e.target.style.color='#6366f1'}
                  onMouseOut={e => e.target.style.color='#64748b'}
                >{s.title}</a>
              ))}
            </nav>
          </div>

          {/* Main content */}
          <div style={{display:'flex', flexDirection:'column', gap:40}}>
            {SECTIONS.map(s => (
              <div key={s.id} id={s.id}>
                <h2 style={{fontSize:19, fontWeight:700, color:'#0f172a', margin:'0 0 14px', paddingBottom:10, borderBottom:'1px solid #f1f5f9'}}>{s.title}</h2>
                {s.body && <p style={{fontSize:15, color:'#475569', lineHeight:1.75, margin:'0 0 12px'}}>{s.body}</p>}
                {s.points && (
                  <ul style={{margin:0, paddingLeft:20, display:'flex', flexDirection:'column', gap:8}}>
                    {s.points.map(p => <li key={p} style={{fontSize:14, color:'#475569', lineHeight:1.6}}>{p}</li>)}
                  </ul>
                )}
                {s.subpoints && s.subpoints.map(sub => (
                  <div key={sub.heading} style={{marginTop:16}}>
                    <h3 style={{fontSize:14, fontWeight:700, color:'#334155', margin:'0 0 8px'}}>{sub.heading}</h3>
                    <ul style={{margin:0, paddingLeft:20, display:'flex', flexDirection:'column', gap:6}}>
                      {sub.items.map(p => <li key={p} style={{fontSize:14, color:'#475569', lineHeight:1.6}}>{p}</li>)}
                    </ul>
                  </div>
                ))}
                {s.table && (
                  <table style={{width:'100%', borderCollapse:'collapse', fontSize:14, marginTop:8}}>
                    <thead><tr>
                      {['Plan', 'Uptime SLA', 'Support'].map(h => <th key={h} style={{textAlign:'left', padding:'10px 12px', background:'#f8fafc', borderBottom:'2px solid #e2e8f0', color:'#64748b', fontWeight:700, fontSize:12, textTransform:'uppercase', letterSpacing:.5}}>{h}</th>)}
                    </tr></thead>
                    <tbody>{s.table.map((r, i) => (
                      <tr key={i} style={{background: i%2===0?'#fff':'#f8fafc'}}>
                        <td style={{padding:'12px', borderBottom:'1px solid #f1f5f9', fontWeight:700, color:'#334155'}}>{r.plan}</td>
                        <td style={{padding:'12px', borderBottom:'1px solid #f1f5f9', color:'#6366f1', fontWeight:600}}>{r.uptime}</td>
                        <td style={{padding:'12px', borderBottom:'1px solid #f1f5f9', color:'#475569'}}>{r.support}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <section style={{background:'#f8fafc', padding:'40px 24px', textAlign:'center'}}>
        <p style={{color:'#64748b', fontSize:14, margin:'0 0 16px'}}>
          Questions about these Terms? Contact our legal team.
        </p>
        <div style={{display:'flex', gap:12, justifyContent:'center'}}>
          <Link to="/website/contact" className="dgw-btn dgw-btn-primary">Contact us</Link>
          <Link to="/website/privacy" className="dgw-btn dgw-btn-ghost">Privacy Policy</Link>
        </div>
      </section>
    </div>
  );
}
